import html2canvas from 'html2canvas';
import type jsPDF from 'jspdf';

/** Canonical light-theme hex fallbacks for oklch tokens (html2canvas cannot parse oklch). */
const OKLCH_TO_HEX: Record<string, string> = {
  'oklch(90% 0.006 280)': '#ecf0f1',
  'oklch(52% 0.18 305)': '#8e44ad',
  'oklch(42% 0.16 305)': '#7a3796',
  'oklch(52% 0.18 305 / 0.12)': 'rgba(142, 68, 173, 0.12)',
  'oklch(52% 0.18 305 / 0.08)': 'rgba(142, 68, 173, 0.08)',
  'oklch(52% 0.18 305 / 0.4)': 'rgba(142, 68, 173, 0.4)',
  'oklch(58% 0.16 145 / 0.1)': 'rgba(34, 197, 94, 0.1)',
  'oklch(38% 0.12 145)': '#15803d',
};

const OKLCH_PATTERN = /oklch\([^)]+\)/g;

export interface AppendHtmlElementToPdfOptions {
  marginMm?: number;
  scale?: number;
}

function replaceOklchInStyle(value: string): string {
  let result = value;
  for (const [oklch, hex] of Object.entries(OKLCH_TO_HEX)) {
    result = result.split(oklch).join(hex);
  }
  result = result.replace(OKLCH_PATTERN, (match) => OKLCH_TO_HEX[match] ?? '#8e44ad');
  return result;
}

/** Walk cloned DOM and swap oklch color values to hex for html2canvas compatibility. */
export function sanitizeOklchForCapture(root: HTMLElement): void {
  const elements = root.querySelectorAll('*');
  for (const el of elements) {
    const htmlEl = el as HTMLElement;
    if (htmlEl.style?.cssText) {
      htmlEl.style.cssText = replaceOklchInStyle(htmlEl.style.cssText);
    }
    for (const attr of ['class', 'style']) {
      const val = htmlEl.getAttribute(attr);
      if (val && val.includes('oklch')) {
        htmlEl.setAttribute(attr, replaceOklchInStyle(val));
      }
    }
  }
  if (root.style?.cssText) {
    root.style.cssText = replaceOklchInStyle(root.style.cssText);
  }
}

/** Wait for web fonts and images inside `root` before capture. */
export async function waitForCaptureReady(root: HTMLElement): Promise<void> {
  await document.fonts?.ready;

  const images = Array.from(root.querySelectorAll('img'));
  await Promise.all(
    images.map(
      (img) =>
        new Promise<void>((resolve) => {
          if (img.complete) {
            resolve();
            return;
          }
          img.onload = () => resolve();
          img.onerror = () => resolve();
        }),
    ),
  );

  await new Promise((resolve) => setTimeout(resolve, 100));
}

/**
 * Capture an HTML element as paginated JPEG pages appended to an existing jsPDF doc.
 */
export async function appendHtmlElementToPdf(
  doc: jsPDF,
  element: HTMLElement,
  options?: AppendHtmlElementToPdfOptions,
): Promise<jsPDF> {
  const marginMm = options?.marginMm ?? 10;
  const scale = options?.scale ?? 2;

  await waitForCaptureReady(element);

  const containerRect = element.getBoundingClientRect();
  const containerWidth = containerRect.width || element.offsetWidth || 1;
  let pageBreaksPx: number[] = [];

  const canvas = await html2canvas(element, {
    scale,
    useCORS: true,
    logging: false,
    backgroundColor: '#fcfcfc',
    onclone: (_doc, clonedElement) => {
      sanitizeOklchForCapture(clonedElement);

      const clonedRect = clonedElement.getBoundingClientRect();
      const breakElements = Array.from(
        clonedElement.querySelectorAll('.page-break-before, [data-page-break="before"]'),
      );

      pageBreaksPx = Array.from(
        new Set(
          breakElements
            .map((el) => {
              const elRect = el.getBoundingClientRect();
              const relativeY = elRect.top - clonedRect.top;
              return Math.round(relativeY * scale);
            })
            .filter((y) => y > 10),
        ),
      ).sort((a, b) => a - b);
    },
  });

  const pageWidthMm = doc.internal.pageSize.getWidth();
  const pageHeightMm = doc.internal.pageSize.getHeight();
  const contentWidthMm = pageWidthMm - marginMm * 2;
  const contentHeightMm = pageHeightMm - marginMm * 2;

  const imgWidthPx = canvas.width;
  const imgHeightPx = canvas.height;
  const sliceHeightPx = Math.floor((contentHeightMm / contentWidthMm) * imgWidthPx);
  const scaleRatio = imgWidthPx / (containerWidth || 1);

  // Extract anchor tags to add interactive PDF link annotations
  const anchorElements = Array.from(element.querySelectorAll<HTMLAnchorElement>('a[href]'));
  const linksInfo = anchorElements
    .map((a) => {
      const rawHref = a.getAttribute('href');
      if (!rawHref || rawHref.startsWith('javascript:')) return null;
      const href =
        typeof window !== 'undefined' && !rawHref.startsWith('http://') && !rawHref.startsWith('https://') && !rawHref.startsWith('mailto:')
          ? new URL(rawHref, window.location.origin).href
          : rawHref;

      const rect = a.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return null;

      const relativeX = rect.left - containerRect.left;
      const relativeY = rect.top - containerRect.top;

      return {
        href,
        xPx: relativeX * scaleRatio,
        yPx: relativeY * scaleRatio,
        wPx: rect.width * scaleRatio,
        hPx: rect.height * scaleRatio,
      };
    })
    .filter((link): link is NonNullable<typeof link> => link !== null);

  let offsetY = 0;
  let pageIndex = 0;

  while (offsetY < imgHeightPx) {
    const maxSliceHeight = Math.min(sliceHeightPx, imgHeightPx - offsetY);
    let sliceHeight = maxSliceHeight;

    // Find the next custom page break that falls within this page's maximum slice height
    const nextBreak = pageBreaksPx.find(
      (breakY) => breakY > offsetY && breakY < offsetY + maxSliceHeight
    );

    if (nextBreak !== undefined) {
      sliceHeight = nextBreak - offsetY;
    }

    const sliceCanvas = document.createElement('canvas');
    sliceCanvas.width = imgWidthPx;
    sliceCanvas.height = sliceHeight;
    const ctx = sliceCanvas.getContext('2d');
    if (!ctx) break;

    ctx.drawImage(canvas, 0, offsetY, imgWidthPx, sliceHeight, 0, 0, imgWidthPx, sliceHeight);
    const imgData = sliceCanvas.toDataURL('image/jpeg', 0.85);
    const renderHeightMm = (sliceHeight * contentWidthMm) / imgWidthPx;

    if (pageIndex > 0) {
      doc.addPage();
    }

    doc.addImage(imgData, 'JPEG', marginMm, marginMm, contentWidthMm, renderHeightMm);

    // Add interactive link annotations for links present in this slice
    if (typeof doc.link === 'function') {
      for (const link of linksInfo) {
        if (link.yPx + link.hPx > offsetY && link.yPx < offsetY + sliceHeight) {
          const linkYInSlice = link.yPx - offsetY;
          const pdfX = marginMm + (link.xPx / imgWidthPx) * contentWidthMm;
          const pdfY = marginMm + (linkYInSlice / sliceHeight) * renderHeightMm;
          const pdfW = (link.wPx / imgWidthPx) * contentWidthMm;
          const pdfH = (link.hPx / sliceHeight) * renderHeightMm;

          doc.link(pdfX, pdfY, Math.max(pdfW, 2), Math.max(pdfH, 2), { url: link.href });
        }
      }
    }

    offsetY += sliceHeight;
    pageIndex += 1;
  }

  return doc;
}
